import { ChangeEvent, useRef, useState } from "react";

import { useUploadPhoto } from "@/features/workers/model/useWorkerProfile";
import {
  ALLOWED_IMAGE_TYPES,
  IMAGE_ACCEPT_ATTR,
  validateUpload,
} from "@/shared/lib/uploadValidation";

interface Props {
  photoUrl: string | null;
}

export function PhotoUpload({ photoUrl }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const upload = useUploadPhoto();
  const [clientError, setClientError] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    const err = validateUpload(file, ALLOWED_IMAGE_TYPES);
    if (err) {
      setClientError(err);
      return;
    }
    setClientError(null);
    setLoadFailed(false);
    upload.mutate(file);
  };

  return (
    <div className="flex items-center gap-4">
      <div className="h-24 w-24 overflow-hidden rounded-full border bg-muted">
        {photoUrl && !loadFailed ? (
          <img
            key={photoUrl}
            src={photoUrl}
            alt="Profile"
            className="h-full w-full object-cover"
            onError={() => setLoadFailed(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
            No photo
          </div>
        )}
      </div>
      <div className="space-y-1">
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={upload.isPending}
          className="rounded-md border border-input px-3 py-2 text-sm hover:bg-surface-sunken disabled:opacity-50"
        >
          {upload.isPending ? "Uploading..." : photoUrl ? "Replace photo" : "Upload photo"}
        </button>
        <p className="text-xs text-copy-muted">JPG, PNG, WebP, GIF, BMP, or HEIC. Max 5 MB.</p>
        {clientError && <p className="text-xs text-destructive-foreground">{clientError}</p>}
        {upload.error && <p className="text-xs text-destructive-foreground">Upload failed</p>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={IMAGE_ACCEPT_ATTR}
        className="hidden"
        onChange={handleChange}
      />
    </div>
  );
}
